/**
 * Tech Tool — Line Card Brand Anchors + Quote Prefill
 * File: assets/tt-lc-brand-anchors.js
 *
 * The 228 brand rows in sections/lc-directory.liquid are hardcoded markup with
 * no per-brand id and an identical quote CTA. This script derives both at
 * runtime:
 *
 *   1. Gives every row  id="lc-brand-<slug>"  so it can be linked to directly.
 *   2. Rewrites each "Request quote" href to carry &manufacturer=<Brand Name>,
 *      which sections/contact-form.liquid already reads (initIntentPrefill) to
 *      prefill #ci-quote-mfr and fire its "Details added." toast.
 *   3. Gives each CTA a distinct aria-label (WCAG 2.4.4 — 228 identical
 *      "Request quote" links are indistinguishable to a screen reader).
 *   4. Handles arriving at #lc-brand-<slug>: clears any active filter that would
 *      hide the target, then scrolls and moves focus.
 *   5. Handles arriving with ?q=<term>: seeds the directory's own search box so
 *      "See all N matching lines" from site search lands pre-filtered.
 *
 * On (5): the directory's filter is wired by an inline script in the section,
 * and a single dispatch on load is unreliable — it can fire before that listener
 * is attached, leaving the box populated but nothing filtered. So the dispatch
 * is retried until the visible count actually moves off the unfiltered total.
 *
 * Slug rule must stay in sync with any server-side generator:
 *   name.toLowerCase() -> non-alphanumeric runs become "-" -> trim leading/trailing "-"
 *
 * Vanilla JS, no dependencies. Idempotent — safe to run again on section reload.
 */

(function () {
  'use strict';

  var ANCHOR_PREFIX = 'lc-brand-';
  var DIRECTORY_HASH = /^#(?:lc-brand-.+|lc-dir-(?:[a-z]|0-9))$/;
  var queryPrefillTimer = 0;
  var anchorFrame = 0;

  function slugify(name) {
    return String(name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function brandNameFrom(row) {
    var link = row.querySelector('.lc-dir-row__brand-link');
    if (!link) return '';
    // textContent picks up the anchor's text only; the SWISS tag sits outside it
    // and the trailing external-link <svg> contributes no text.
    return (link.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function enhanceRows(root) {
    var rows = root.querySelectorAll('.lc-dir-row');
    if (!rows.length) return 0;

    var used = Object.create(null);
    var count = 0;

    Array.prototype.forEach.call(rows, function (row) {
      var name = brandNameFrom(row);
      if (!name) return;

      var slug = slugify(name);
      if (!slug) return;

      // Defensive: if two brands ever collide, suffix rather than duplicate an id.
      if (used[slug]) {
        var n = 2;
        while (used[slug + '-' + n]) n++;
        slug = slug + '-' + n;
      }
      used[slug] = true;

      if (!row.id) row.id = ANCHOR_PREFIX + slug;
      row.setAttribute('data-lc-slug', slug);
      row.setAttribute('data-lc-name', name);

      var cta = row.querySelector('[data-lc-quote]');
      if (cta) {
        var href = cta.getAttribute('href') || '/pages/contact?type=quote';
        if (href.indexOf('manufacturer=') === -1) {
          href += (href.indexOf('?') === -1 ? '?' : '&') +
                  'manufacturer=' + encodeURIComponent(name);
          cta.setAttribute('href', href);
        }
        cta.setAttribute('aria-label', 'Request quote for ' + name);
      }

      count++;
    });

    return count;
  }

  function scrollToEl(target, smooth) {
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.requestAnimationFrame(function () {
      target.scrollIntoView({
        behavior: (reduceMotion || !smooth) ? 'auto' : 'smooth',
        block: 'start'
      });
    });
  }

  /* Arriving from site search with ?q=<term>: seed the directory's own search
     box so the visitor sees the same matches they clicked through from. */
  function applyQueryPrefill(root) {
    var term = '';
    try {
      term = (new URLSearchParams(window.location.search).get('q') || '').trim();
    } catch (e) { return false; }
    if (!term) return false;

    var search = root.querySelector('#lc-dir-search');
    if (!search) return false;

    var countEl = root.querySelector('#lc-dir-count-num');
    var totalRows = root.querySelectorAll('.lc-dir-row').length;
    var tries = 0;
    var MAX_TRIES = 12;

    (function attempt() {
      search.value = term;
      search.dispatchEvent(new Event('input', { bubbles: true }));
      tries++;

      queryPrefillTimer = window.setTimeout(function () {
        var shown = countEl ? parseInt(countEl.textContent, 10) : NaN;
        var applied = !isNaN(shown) && shown !== totalRows;
        if (!applied && tries < MAX_TRIES) attempt();
      }, 120);
    })();

    var directory = document.getElementById('lc-directory');
    if (directory) scrollToEl(directory, true);

    return true;
  }

  function syncCurrentLetter(target) {
    var group = target && (target.classList.contains('lc-dir-letter-group') ? target : target.closest('.lc-dir-letter-group'));
    document.querySelectorAll('.lc-jump-link').forEach(function (link) {
      if (group && link.hash === '#' + group.id) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }

  // A filtered-out letter cannot be a native scroll target. Reveal it first,
  // then scroll after the directory's filter render has completed.
  function resolveHash(root) {
    var hash = window.location.hash || '';
    if (!DIRECTORY_HASH.test(hash)) { syncCurrentLetter(null); return; }
    var target = document.getElementById(hash.slice(1));
    if (!target || !root.contains(target)) return;
    window.clearTimeout(queryPrefillTimer);
    window.cancelAnimationFrame(anchorFrame);

    var isBrand = hash.indexOf('#' + ANCHOR_PREFIX) === 0;
    if (isBrand || !target.getClientRects().length) {
      var allChip = root.querySelector('[data-lc-chip="all"]');
      if (allChip) allChip.click();
      var search = root.querySelector('#lc-dir-search');
      if (search && search.value) {
        search.value = '';
        search.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    anchorFrame = window.requestAnimationFrame(function () {
      syncCurrentLetter(target);
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (isBrand) {
        target.classList.add('is-lc-targeted');
        window.setTimeout(function () { target.classList.remove('is-lc-targeted'); }, 2400);
      }
    });
  }

  function init() {
    var root = document.querySelector('#lc-directory');
    if (!root) return;
    if (enhanceRows(root) === 0) return;

    // A brand anchor is more specific than a search term, so it wins.
    if (DIRECTORY_HASH.test(window.location.hash || '')) {
      resolveHash(root);
      return;
    }

    applyQueryPrefill(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Repeated clicks on the same letter do not fire hashchange. Handle these
  // links directly and retain browser Back/Forward history for distinct jumps.
  document.addEventListener('click', function (event) {
    var link = event.target.closest('.lc-jump-link');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var root = document.getElementById('lc-directory');
    if (!root || !document.getElementById(link.hash.slice(1))) return;
    event.preventDefault();
    if (window.location.hash !== link.hash) window.history.pushState(null, '', link.hash);
    resolveHash(root);
  });

  // Re-run when the theme editor reloads the section, and on in-page hash changes.
  document.addEventListener('shopify:section:load', init);
  window.addEventListener('hashchange', function () {
    var root = document.querySelector('#lc-directory');
    if (root) resolveHash(root);
  });
})();
